import { browser } from "$app/environment";

export interface GRecaptcha {
    ready: (callback: () => void) => void;
    execute: (siteKey: string, options: { action: string }) => Promise<string>;
}

declare global {
    interface Window {
        grecaptcha?: GRecaptcha;
    }
}

let recaptchaScriptPromise: Promise<GRecaptcha> | null = null;

export function loadRecaptcha(siteKey: string): Promise<GRecaptcha> {
    if (!browser) {
        return Promise.reject(new Error("reCAPTCHA is only available in the browser"));
    }

    if (window.grecaptcha) {
        return Promise.resolve(window.grecaptcha);
    }

    if (recaptchaScriptPromise) {
        return recaptchaScriptPromise;
    }

    recaptchaScriptPromise = new Promise((resolve, reject) => {
        const existingScript = document.querySelector<HTMLScriptElement>(
            `script[src^="https://www.google.com/recaptcha/api.js?render="]`,
        );

        const onLoad = () => {
            if (window.grecaptcha) {
                resolve(window.grecaptcha);
                return;
            }

            reject(new Error("reCAPTCHA script loaded without exposing grecaptcha"));
        };

        if (existingScript) {
            existingScript.addEventListener("load", onLoad, { once: true });
            existingScript.addEventListener(
                "error",
                () => reject(new Error("Failed to load reCAPTCHA")),
                { once: true },
            );
            return;
        }

        const script = document.createElement("script");
        script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
        script.async = true;
        script.defer = true;
        script.addEventListener("load", onLoad, { once: true });
        script.addEventListener("error", () => reject(new Error("Failed to load reCAPTCHA")), {
            once: true,
        });
        document.head.appendChild(script);
    });

    return recaptchaScriptPromise;
}
